// src/components/ResetPasswordPage.js - Branded password reset page
// Users land here after clicking the reset link in their email
import { useState, useEffect } from "react";
import Icon from "./icons/Icon";
import { Link, useSearchParams } from "react-router-dom";
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
} from "firebase/auth";
import { auth } from "../firebase/config";
import { AuthBrandingPanel, AuthMobileHeader } from "./auth/AuthBrandingPanel";

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState("LOADING"); // LOADING | INVALID | FORM | SUCCESS
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    if (!oobCode) {
      setView("INVALID");
      return;
    }

    if (mode === "verifyEmail") {
      applyActionCode(auth, oobCode)
        .then(() => {
          setView("EMAIL_VERIFIED");
        })
        .catch(() => {
          setView("INVALID");
        });
      return;
    }

    if (mode !== "resetPassword") {
      setView("INVALID");
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((userEmail) => {
        setEmail(userEmail);
        setView("FORM");
      })
      .catch(() => {
        setView("INVALID");
      });
  }, [mode, oobCode]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (!newPassword) {
      setError("New password is required.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (!confirmPassword) {
      setError("Please confirm your new password.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setView("SUCCESS");
    } catch (err) {
      setError(getResetErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Left Branding Panel (Desktop Only) */}
      <AuthBrandingPanel />

      {/* Right Form Panel */}
      <div className="auth-form-panel">
        <div className="auth-form-container">
          <AuthMobileHeader />

          {/* ===== LOADING VIEW ===== */}
          {view === "LOADING" && (
            <div className="auth-form-header">
              <div className="auth-loading-spinner"></div>
              <h2 className="auth-form-title">Verifying reset link</h2>
              <p className="auth-form-subtitle">
                Please wait while we verify your password reset link...
              </p>
            </div>
          )}

          {/* ===== INVALID LINK VIEW ===== */}
          {view === "INVALID" && (
            <>
              <div className="auth-form-header">
                <div className="auth-error-icon">
                  <Icon name="x" />
                </div>
                <h2 className="auth-form-title">Invalid or expired link</h2>
                <p className="auth-form-subtitle">
                  This password reset link is no longer valid. It may have expired or already been used.
                </p>
              </div>

              <p className="auth-reset-note">
                Please request a new password reset link from the login page.
              </p>

              <div className="auth-back-link">
                <Link to="/login">
                  <Icon name="arrow-left" />
                  Back to Login
                </Link>
              </div>
            </>
          )}

          {/* ===== RESET FORM VIEW ===== */}
          {view === "FORM" && (
            <>
              <div className="auth-form-header">
                <h2 className="auth-form-title">Set new password</h2>
                <p className="auth-form-subtitle">
                  Create a new password for your account.
                </p>
              </div>

              <p className="auth-reset-email">
                Resetting password for <strong>{email}</strong>
              </p>

              <form onSubmit={handleResetPassword} className="form" noValidate>
                {error && (
                  <div className="alert alert--error mb-6">
                    <div className="alert__content">{error}</div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="new-password" className="form-label form-label--required">
                    New Password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="form-input"
                    placeholder="Enter new password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirm-password" className="form-label form-label--required">
                    Confirm Password
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="form-input"
                    placeholder="Re-enter new password"
                  />
                </div>

                <div className="form-group">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`btn btn--ph-primary btn--full btn--md ${
                      loading ? "btn--loading" : ""
                    }`}
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              </form>

              <div className="auth-back-link">
                <Link to="/login">
                  <Icon name="arrow-left" />
                  Back to Login
                </Link>
              </div>
            </>
          )}

          {/* ===== EMAIL VERIFIED VIEW ===== */}
          {view === "EMAIL_VERIFIED" && (
            <>
              <div className="auth-form-header">
                <div className="auth-success-icon">
                  <Icon name="check" />
                </div>
                <h2 className="auth-form-title">Email verified!</h2>
                <p className="auth-form-subtitle">
                  Your email has been verified successfully. You can now sign in to your account.
                </p>
              </div>

              <div className="form-group">
                <Link
                  to="/login"
                  className="btn btn--ph-primary btn--full btn--md"
                  style={{ textDecoration: "none" }}
                >
                  Go to Login
                </Link>
              </div>
            </>
          )}

          {/* ===== SUCCESS VIEW ===== */}
          {view === "SUCCESS" && (
            <>
              <div className="auth-form-header">
                <div className="auth-success-icon">
                  <Icon name="check" />
                </div>
                <h2 className="auth-form-title">Password reset successful</h2>
                <p className="auth-form-subtitle">
                  Your password has been updated. You can now sign in with your new password.
                </p>
              </div>

              <div className="form-group">
                <Link
                  to="/login"
                  className="btn btn--ph-primary btn--full btn--md"
                  style={{ textDecoration: "none" }}
                >
                  Go to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const getResetErrorMessage = (errorCode) => {
  switch (errorCode) {
    case "auth/expired-action-code":
      return "This reset link has expired. Please request a new one.";
    case "auth/invalid-action-code":
      return "This reset link is invalid or has already been used.";
    case "auth/weak-password":
      return "Password is too weak. Please choose a stronger password.";
    default:
      return "An error occurred. Please try again.";
  }
};
